/**
 * RegionalIntensityLayer — Phase 4b true receptor-sampled heatmap.
 *
 * Where RegionalAirshedLayer draws one polygon per plant and lets
 * visual overlap approximate intensity, this layer renders a single
 * PNG tile pyramid computed offline by `build_intensity_raster.py`
 * +  `build_intensity_tiles.py`:
 *
 *     intensity[receptor] = population[receptor]
 *         * Σ_plants (weight_p * ISRM_PM25[layer_p, src_p, receptor])
 *
 * That sum across plants is what turns "which airshed is here?"
 * (polygon soup) into "how much downwind PM intake actually lands
 * here?" (true hotspot). Pittsburgh / Louisville / Chicago light up
 * from the sum of many midwestern plumes on dense population.
 *
 * The layer is a thin wrapper around Leaflet's TileLayer:
 *   - URL template comes from the backend manifest
 *   - zoom range clamped to the manifest's min/max
 *   - no client-side rendering (pre-rendered server-side)
 *
 * Falls back silently (renders nothing, parent re-renders the
 * polygon layer) when the backend returns null.
 */

import { useMemo, useState } from "react";
import {
  CircleMarker,
  Popup,
  TileLayer,
  Tooltip,
  useMapEvents,
} from "react-leaflet";
import type { LatLng } from "leaflet";
import type {
  DisplacedGeneratorEnriched,
  IntensityTileResponse,
} from "../../model/domain/types";
import {
  MetricKey,
  METRIC_LABEL,
  fmtMetric,
  getFuelColor,
  getFuelDisplayName,
  pickMetric,
  stateName,
} from "../helpers";

interface Props {
  tiles: IntensityTileResponse;
  plants: DisplacedGeneratorEnriched[];
  metric: MetricKey;
  onPlantHover?: (plantId: string | number | null) => void;
}

export function RegionalIntensityLayer({
  tiles,
  plants,
  metric,
  onPlantHover,
}: Props) {
  // Plant dots render on top so customers can still see which plants
  // drive the heat. We keep the isolate behaviour from the polygon
  // layer (click a dot to highlight it) for visual parity — but since
  // tiles are one baked raster the highlight is just a marker halo.
  const [highlighted, setHighlighted] = useState<string | number | null>(null);

  const plantsWithCoords = useMemo(
    () =>
      plants
        .filter((p) => p.latitude && p.longitude)
        .sort(
          (a, b) =>
            pickMetric(b, "regional", metric) - pickMetric(a, "regional", metric),
        ),
    [plants, metric],
  );

  return (
    <>
      <TileLayer
        url={tiles.tile_url_template}
        minZoom={tiles.min_zoom}
        maxZoom={tiles.max_zoom}
        tileSize={tiles.tile_size || 256}
        opacity={0.78}
        // Don't pin keepBuffer; larger values rack up requests on big
        // zoom-outs. Default (2) is fine for this fixed-extent dataset.
        zIndex={350}
        attribution={
          tiles.citation ||
          "InMAP ISRM v1.2.1 (Tessum, Hill & Marshall 2017)"
        }
      />

      {/* Plant dots — same symbology as the polygon layer so customers
          can cross-reference between the two views without recolouring. */}
      {plantsWithCoords.map((p) => {
        const fuelColor = getFuelColor(p.fuel_type);
        const isHighlighted =
          highlighted !== null && String(highlighted) === String(p.plant_id);
        return (
          <CircleMarker
            key={`intensity-plant-dot-${p.plant_id}`}
            center={[p.latitude, p.longitude]}
            radius={isHighlighted ? 6 : 4}
            pathOptions={{
              fillColor: fuelColor,
              color: isHighlighted ? "#facc15" : "#ffffff",
              weight: isHighlighted ? 2 : 1,
              fillOpacity: 0.95,
              opacity: 1,
            }}
            eventHandlers={{
              mouseover: () => onPlantHover?.(p.plant_id),
              mouseout: () => onPlantHover?.(null),
              click: () =>
                setHighlighted((prev) =>
                  String(prev) === String(p.plant_id) ? null : p.plant_id,
                ),
            }}
          >
            <Tooltip direction="top" offset={[0, -6]}>
              <div className="text-[12px] text-gray-900">
                <div className="font-semibold">{p.plant_name}</div>
                <div className="text-gray-600">
                  {getFuelDisplayName(p.fuel_type)} ·{" "}
                  {p.county ? `${p.county}, ` : ""}
                  {stateName(p.state)}
                </div>
                <div className="mt-0.5 text-gray-800 font-semibold">
                  {fmtMetric(pickMetric(p, "regional", metric), metric)}{" "}
                  {METRIC_LABEL[metric]} regional
                </div>
              </div>
            </Tooltip>
          </CircleMarker>
        );
      })}

      {/* Click anywhere → popup with the intensity interpretation. Matches
          the polygon layer's click behaviour so the two views feel
          consistent; here the "matches" list is just "the top N
          contributing plants nearby" since the tile sum is baked in and
          we can't re-derive contributors client-side. */}
      <IntensityClickPopup
        plants={plantsWithCoords}
        metric={metric}
        tiles={tiles}
      />
    </>
  );
}

// ────────────────────────────────────────────────────────────────────
// Click popup — list the top-value plants within ~500 km of the click
// ────────────────────────────────────────────────────────────────────

function IntensityClickPopup({
  plants,
  metric,
  tiles,
}: {
  plants: DisplacedGeneratorEnriched[];
  metric: MetricKey;
  tiles: IntensityTileResponse;
}) {
  const [clicked, setClicked] = useState<LatLng | null>(null);
  useMapEvents({
    click: (e) => setClicked(e.latlng),
  });

  const nearby = useMemo(() => {
    if (!clicked) return [];
    const R = 6371;
    const toRad = (d: number) => (d * Math.PI) / 180;
    const out: Array<{ p: DisplacedGeneratorEnriched; value: number; km: number }> = [];
    for (const p of plants) {
      const dLat = toRad(p.latitude - clicked.lat);
      const dLng = toRad(p.longitude - clicked.lng);
      const s =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(clicked.lat)) *
          Math.cos(toRad(p.latitude)) *
          Math.sin(dLng / 2) ** 2;
      const km = 2 * R * Math.asin(Math.sqrt(s));
      if (km < 500) {
        out.push({ p, value: pickMetric(p, "regional", metric), km });
      }
    }
    out.sort((a, b) => b.value - a.value);
    return out;
  }, [clicked, plants, metric]);

  if (!clicked) return null;
  return (
    <Popup
      position={clicked}
      className="spatial-popup"
      eventHandlers={{ remove: () => setClicked(null) }}
      maxWidth={320}
    >
      <div className="text-sm text-gray-800 min-w-[260px]">
        <p className="font-semibold text-gray-900 mb-1">
          Intensity at this point
        </p>
        <p className="text-[11px] text-gray-600 leading-snug mb-2">
          Colour is the summed population-weighted PM2.5 intake contribution
          from ALL {tiles.generator_count ?? "displaced"} plants — the
          hotspot reflects where many plumes land on many people, not
          one plant's airshed.
        </p>
        {nearby.length === 0 ? (
          <p className="text-gray-500 text-xs">
            No displaced plant within 500 km of this click — any
            colour shown here comes from more distant plumes.
          </p>
        ) : (
          <>
            <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">
              Nearest plants (≤500 km)
            </p>
            <ul className="space-y-1">
              {nearby.slice(0, 6).map(({ p, value, km }) => {
                const fuelColor = getFuelColor(p.fuel_type);
                return (
                  <li
                    key={`near-${p.plant_id}`}
                    className="border-b border-gray-100 last:border-0 pb-1 last:pb-0"
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-medium text-gray-900 leading-tight">
                        <span
                          className="inline-block w-2 h-2 rounded-full mr-1 -mb-0.5"
                          style={{ backgroundColor: fuelColor }}
                        />
                        {p.plant_name}
                      </span>
                      <span className="text-gray-900 font-semibold whitespace-nowrap">
                        {fmtMetric(value, metric)}
                      </span>
                    </div>
                    <div className="text-[11px] text-gray-500 leading-tight">
                      {getFuelDisplayName(p.fuel_type)} · {stateName(p.state)}{" "}
                      · {Math.round(km)} km
                    </div>
                  </li>
                );
              })}
            </ul>
            {nearby.length > 6 && (
              <p className="mt-1 text-[11px] text-gray-500">
                + {nearby.length - 6} more plants within 500 km
              </p>
            )}
          </>
        )}
      </div>
    </Popup>
  );
}

// ────────────────────────────────────────────────────────────────────
// In-map legend chip — shown by the parent card when the heatmap view
// is active. Exported so GeneratorAttributionCard can swap it in for
// the polygon RegionalLegend.
// ────────────────────────────────────────────────────────────────────

export function IntensityLegend({
  tiles,
}: {
  tiles: IntensityTileResponse;
}) {
  // Matches the build_intensity_tiles.py ramp — kept in sync manually.
  const gradient =
    "linear-gradient(to right, rgb(68,1,84), rgb(59,82,139), rgb(33,145,140), rgb(94,201,98), rgb(253,231,37))";
  return (
    <div
      className="absolute top-2 right-2 z-[500] rounded-md bg-surface-700/90 border border-border px-2.5 py-2 text-[10px] text-text-secondary leading-tight backdrop-blur-sm"
      style={{ pointerEvents: "none" }}
    >
      <div className="uppercase tracking-wider text-text-muted mb-1">
        PM2.5 intake contribution
      </div>
      <div
        className="h-1.5 rounded-full"
        style={{ width: 140, background: gradient }}
      />
      <div className="flex justify-between" style={{ width: 140 }}>
        <span>low</span>
        <span>hotspot</span>
      </div>
      <div className="mt-1 text-text-muted">
        {tiles.generator_count
          ? `Summed across ${tiles.generator_count} plants`
          : "Summed across all displaced plants"}
      </div>
    </div>
  );
}
